import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Col } from "react-bootstrap";
import { IoCloseSharp } from "react-icons/io5";
import DatePicker from "react-datepicker";

import {
  AppliedFiltersWrapper,
  Container,
  ContainerButton,
  ContainerCheckbox,
  FilterItem,
  FilterKey,
  FiltersContainer,
  FilterTitle,
  FilterValue,
  LabelCheckbox,
} from "./styles";
import {
  BlueTitle,
  ButtonGroupEnd,
  GrayTitle,
  ModalBody,
  ModalBodyContainer,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  StyledHR,
  TextError,
  TitleAndButton,
} from "../../../shared/styles/global.styles";
import {
  Table,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/Table/styles";
import Button from "../../../components/Button/button";
import { Label } from "../../../components/Label/styles";
import SelectComponent from "../../../components/Select/select";
import { DatePickerInputContainer } from "../../../components/Input/styles";
import { Lotacao } from "../../../shared/interfaces/DadosFinanceirosInterface";
import lotacaoService from "../../../services/LotacaoService";
import RelatorioService from "../../../services/RelatorioService";
import { LocalPagamentoDto } from "../../../shared/dtos/LocalPagamento.dto";
import localPagamentoService from "../../../services/LocalPagamentoService";
import { GetFinancialRecordDto } from "../../../shared/dtos/FichaFinanceira.dto";
import { GetContribuinteDto } from "../../../shared/dtos/Contribuinte.dto";
import { formatarData } from "../../../utils/dateUtils/dateUtils";
import { formatCurrency } from "../../../utils/formatCurrency/formatCurrency";
import { Checkbox } from "@mui/material";
import FichaFinanceiraService from "../../../services/FichaFinanceiraService";
import { progressBarSwalAlert } from "../../../components/ProgressBarSwalAlert/ProgressBarSwalAlert";
import { Colors } from "../../../shared/colors/colors";
import requestInProgressService from "../../../services/RequestInProgressService";

export interface ContribuinteParcelas {
  contribuinte: GetContribuinteDto;
  parcelas: GetFinancialRecordDto[];
}

export interface ItemFinanceiro {
  categoria: string;
  valor: string;
  porcentagem: string;
}

export interface ResumoFinanceiro {
  resumoFinanceiro: ItemFinanceiro[];
  fichas: GetFinancialRecordDto[];
  summaryPayment: {
    idFormaPagamento: number;
    formaPagamento: string;
    valorBoleto: number;
    valorAjuste: number;
    valorRecebido: number;
    totalParcelas: number;
  }[];
}

const RelatorioRecebimentos: React.FC = () => {
  const [lotacoes, setLotacoes] = useState<Lotacao[]>([]);
  const [selectLotacao, setSelectLotacao] = useState<Lotacao>();
  const [appliedLotacao, setAppliedLotacao] = useState<Lotacao>();
  const [locaisPagamento, setLocaisPagamento] = useState<LocalPagamentoDto[]>(
    []
  );
  const [selectLocalPagamento, setSelectLocalPagamento] =
    useState<LocalPagamentoDto>();
  const [appliedLocalPagamento, setAppliedLocalPagamento] =
    useState<LocalPagamentoDto>();
  const [dateRange, setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, undefined]);
  const [appliedDateRange, setAppliedDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, undefined]);

  const [showModalReport, setShowModalReport] = useState<boolean>(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [isRequest, setIsRequest] = useState<boolean>(false);
  const [parcelasPorContribuinte, setParcelasPorContribuinte] = useState<
    ContribuinteParcelas[]
  >([]);
  const [blobAnalyticPDF, setBlobAnalyticPDF] = useState<Blob>();
  const [blobResumePDF, setBlobResumePDF] = useState<Blob>();

  const clearError = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    setInvalidFields((prev) => prev.filter((field) => field !== name));
  };

  const fetchLotacoes = async () => {
    try {
      const response = await lotacaoService.getAllLotacoes();
      setLotacoes(response);
    } catch (error) {
      Swal.fire({
        title: "Erro",
        text: "Erro ao buscar lotações. Por favor, tente novamente.",
        icon: "error",
        confirmButtonText: "Fechar",
        allowOutsideClick: false,
      });
    }
  };

  const fetchLocaisPagamento = async () => {
    try {
      const response = await localPagamentoService.getAllLocaisPagamento();
      setLocaisPagamento(response);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro ao buscar locais de pagamento",
        allowOutsideClick: false,
      });
    }
  };

  const generateReport = async () => {
    setIsRequest(true);
    try {
      const newInvalidFields: string[] = [];

      if ((!dateRange[0] && !dateRange[1]) || !dateRange[0] || !dateRange[1]) {
        newInvalidFields.push("dataVencimento");
      } else {
        if (dateRange[1] < dateRange[0]) {
          newInvalidFields.push("dataVencimento");
        }
      }

      if (newInvalidFields.length > 0) {
        setInvalidFields(newInvalidFields);

        Swal.fire({
          icon: "error",
          title: "Erro nos campos",
          text: "Por favor, preencha todos os campos obrigatórios.",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        });

        setIsRequest(false);
        return;
      }

      const confirmAlert = await Swal.fire({
        title: "Confirmar",
        text: "Você tem certeza que deseja gerar o relatório de recebimentos?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sim",
        cancelButtonText: "Cancelar",
        allowOutsideClick: false,
      });

      if (confirmAlert.isConfirmed) {
        Swal.fire({
          title: "Gerando relatório...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        if (dateRange[0] && dateRange[1]) {
          await RelatorioService.getRecebimentosReport(
            dateRange[0],
            dateRange[1],
            selectLotacao?.id?.toString() ?? undefined,
            selectLocalPagamento?.id?.toString() ?? undefined
          )
            .then(async (responseRelatorio) => {
              if (responseRelatorio.success) {
                Swal.fire({
                  title: "Processando solicitação",
                  html: progressBarSwalAlert({
                    currentProgress: 0,
                    totalProgress: 0,
                    color: Colors.primary,
                    showProgressText: true,
                  }),
                  showConfirmButton: false,
                  allowOutsideClick: false,
                  didOpen: () => {
                    Swal.showLoading();
                  },
                });

                let stopLoopRelatorio = false;
                let getRequestInProgressRelatorio =
                  await requestInProgressService.getRequestInProgressById(
                    responseRelatorio.data.id
                  );
                await new Promise((resolve) => setTimeout(resolve, 2000));

                while (!stopLoopRelatorio) {
                  getRequestInProgressRelatorio =
                    await requestInProgressService.getRequestInProgressById(
                      responseRelatorio.data.id
                    );

                  if (getRequestInProgressRelatorio.finished) {
                    stopLoopRelatorio = true;
                  } else {
                    Swal.update({
                      html: progressBarSwalAlert({
                        currentProgress:
                          getRequestInProgressRelatorio.currentProgress,
                        totalProgress:
                          getRequestInProgressRelatorio.totalProgress,
                        color: Colors.primary,
                        showProgressText: true,
                        progressText: "Gerando relatório aguarde...",
                      }),
                    });
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                  }
                }
                if (!getRequestInProgressRelatorio.error) {
                  // Se o status da resposta for 'sucesso', exibe uma mensagem de sucesso
                  if (getRequestInProgressRelatorio.responseData) {
                    const relatorioData: any | undefined = JSON.parse(
                      getRequestInProgressRelatorio.responseData
                    );
                    if (relatorioData) {
                      setParcelasPorContribuinte(
                        relatorioData.parcelasPorContribuinte
                      );

                      const getPdfResume = await fetch(
                        `${process.env.REACT_APP_API_URL}pdfs/${relatorioData.pdfResumo}`
                      );
                      if (!getPdfResume.ok) {
                        throw new Error(
                          `Erro ao buscar o PDF: ${getPdfResume.statusText}`
                        );
                      }
                      const pdfBlobResume = await getPdfResume.blob();
                      setBlobResumePDF(pdfBlobResume);

                      const getPdfAnalytic = await fetch(
                        `${process.env.REACT_APP_API_URL}pdfs/${relatorioData.pdfAnalitico}`
                      );
                      if (!getPdfAnalytic.ok) {
                        throw new Error(
                          `Erro ao buscar o PDF: ${getPdfAnalytic.statusText}`
                        );
                      }
                      const pdfBlobAnalytic = await getPdfAnalytic.blob();
                      setBlobAnalyticPDF(pdfBlobAnalytic);

                      await FichaFinanceiraService.deleteManyFileInPdfs([
                        relatorioData.pdfResumo,
                        relatorioData.pdfAnalitico,
                      ]);

                      const { value: result } = await Swal.fire({
                        title: "Sucesso",
                        icon: "success",
                        text: "Relatório gerado com sucesso!",
                        html: `
                              <button id="swal-confirm" class="swal2-confirm swal2-styled">
                                Imprimir - Analítico
                              </button>
                              <button id="swal-cancel" class="swal2-cancel swal2-styled">
                                Imprimir - Resumo
                              </button>
                              <button id="swal-deny" class="swal2-deny swal2-styled">
                                Fechar
                              </button>
                            `,
                        customClass: {
                          actions: "swal2-actions-column", // Coloca os botões em coluna
                        },
                        showConfirmButton: false,
                        showCancelButton: false,
                        showDenyButton: false,
                        focusConfirm: false,
                        allowOutsideClick: false,
                        didOpen: () => {
                          // Adiciona eventos de clique aos botões personalizados
                          const confirmButton =
                            document.getElementById("swal-confirm");
                          const cancelButton =
                            document.getElementById("swal-cancel");
                          const denyButton =
                            document.getElementById("swal-deny");

                          if (confirmButton) {
                            confirmButton.addEventListener("click", () => {
                              imprimirRelatorio(pdfBlobAnalytic);
                              Swal.close(); // Fecha o modal
                            });
                          }

                          if (cancelButton) {
                            cancelButton.addEventListener("click", () => {
                              imprimirRelatorio(pdfBlobResume);
                              Swal.close(); // Fecha o modal
                            });
                          }

                          if (denyButton) {
                            denyButton.addEventListener("click", () => {
                              Swal.close(); // Fecha o modal
                            });
                          }
                        },
                      });
                    }
                  } else {
                    const relatorioData: any | undefined =
                      getRequestInProgressRelatorio.responseData;
                    const resultDeleteFile =
                      await FichaFinanceiraService.deleteManyFileInPdfs([
                        relatorioData.resumoFinanceiro.pdfResumo,
                        relatorioData.resumoFinanceiro.pdfAnalitico,
                      ]);
                    if (resultDeleteFile.success) {
                      Swal.fire({
                        title: "Erro",
                        text: getRequestInProgressRelatorio.message,
                        icon: "error",
                        confirmButtonText: "OK",
                        allowOutsideClick: false,
                      });
                    }
                  }
                } else {
                  Swal.fire({
                    title: "Erro",
                    text: "Ocorreu um erro ao solicitar o cadastro das parcelas. Por favor, tente novamente.",
                    icon: "error",
                    confirmButtonText: "OK",
                    allowOutsideClick: false,
                  });
                }
              } else {
                Swal.fire({
                  icon: "error",
                  title: "Erro ao gerar relatório",
                  text: "Ocorreu um erro ao solicitar o cadastro das parcelas. Por favor, tente novamente.",
                  confirmButtonText: "OK",
                  allowOutsideClick: false,
                });
              }
            })
            .catch((error) => {
              console.error(error);
              Swal.fire({
                icon: "error",
                title: "Erro ao gerar relatório",
                text:
                  error.response?.data?.message || "Erro ao gerar relatório",
                allowOutsideClick: false,
              });
            });
        } else {
          throw new Error("Datas inválidas para gerar o relatório.");
        }
      } else {
        setIsRequest(false);
        Swal.close();
      }
    } catch (error: any) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Erro ao gerar relatório",
        text: error.response?.data?.message || "Erro ao gerar relatório",
        allowOutsideClick: false,
      });
      setIsRequest(false);
    }
    closeReportModal();
    setIsRequest(false);
  };

  const handleDateRangeChange = (
    dates: [Date | undefined, Date | undefined]
  ) => {
    // Convertendo valores null para undefined
    const [start, end] = dates;

    if (start) {
      setDateRange([start, dateRange[1]]);
    }
    if (end) {
      setDateRange([dateRange[0], end]);
    }
  };

  const closeReportModal = () => {
    setShowModalReport(false);
    setInvalidFields([]);
    setIsRequest(false);
  };

  // Função para formatar a entrada manual de datas
  const handleDateInputChange = (e: any) => {
    if (!e || !e.target || typeof e.target.value !== "string") return;

    let value = e.target.value;

    // Não fazer nada se o campo estiver vazio
    if (!value) return;

    // Verificar se o valor contém hífen (separando intervalo de datas)
    const parts = value.split("-");

    if (parts.length > 1) {
      // Processar intervalo de datas (início - fim)
      let startDate = parts[0].trim();
      let endDate = parts[1].trim();

      // Formatar a primeira data
      startDate = formatDateInput(startDate);

      // Formatar a segunda data
      endDate = formatDateInput(endDate);

      // Recompor o valor com o formato correto
      e.target.value = `${startDate} - ${endDate}`;
    } else {
      // Processar uma única data
      const formattedDate = formatDateInput(value);

      // Verificar se a primeira data está completa (10 caracteres = dd/mm/aaaa)
      // Se estiver completa e não for uma operação de apagar (verificar se o valor atual é maior que o anterior)
      if (formattedDate.length === 10 && value.length >= formattedDate.length) {
        // Adicionar o hífen automaticamente
        e.target.value = `${formattedDate} - `;
      } else {
        e.target.value = formattedDate;
      }
    }
  };

  // Função auxiliar para formatar uma data individual
  const formatDateInput = (value: string): string => {
    // Remover caracteres não numéricos
    let numbers = value.replace(/\D/g, "");

    // Limitar a 8 dígitos (ddmmyyyy)
    numbers = numbers.substring(0, 8);

    // Aplicar máscara dd/mm/yyyy
    let formattedValue = "";
    if (numbers.length > 0) {
      formattedValue = numbers.substring(0, Math.min(2, numbers.length));

      if (numbers.length > 2) {
        formattedValue +=
          "/" + numbers.substring(2, Math.min(4, numbers.length));
      }

      if (numbers.length > 4) {
        formattedValue +=
          "/" + numbers.substring(4, Math.min(8, numbers.length));
      }
    }

    return formattedValue;
  };

  // const imprimirRelatorio = async (
  //   data: ResumoFinanceiro,
  //   complete: boolean,
  //   lotacao?: Lotacao,
  //   localPagamento?: LocalPagamentoDto,
  //   dateRange?: [Date | undefined, Date | undefined]
  // ) => {
  //   Swal.fire({
  //     title: "Gerando PDF do relatório...",
  //     allowOutsideClick: false,
  //     didOpen: () => {
  //       Swal.showLoading();
  //     },
  //   });
  //   await new Promise((resolve) => setTimeout(resolve, 1000));

  //   if (checkExistingPDF(complete)) {
  //     return;
  //   }

  //   await pdf(
  //     <RelatorioPDF
  //       data={data}
  //       complete={complete}
  //       lotacao={lotacao?.descricao}
  //       localPagamento={localPagamento?.descricao}
  //       dateRange={{
  //         inicio: dateRange![0]?.toLocaleDateString("pt-BR") || "",
  //         fim: dateRange![1]?.toLocaleDateString("pt-BR") || "",
  //       }}
  //     />
  //   )
  //     .toBlob()
  //     .then((blob) => {
  //       const url = URL.createObjectURL(blob);
  //       window.open(url);
  //       if (complete) {
  //         setAnalyticPDF(blob);
  //       } else {
  //         setResumePDF(blob);
  //       }
  //     })
  //     .finally(() => {
  //       Swal.close();
  //       closeReportModal();
  //     });
  // };

  const imprimirRelatorio = async (pdf: Blob) => {
    try {
      const url = URL.createObjectURL(pdf);
      const newWindow = window.open(url, "_blank");
      if (newWindow) {
        newWindow.focus();
      }
      // Revoga a URL após um pequeno atraso
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return;
    } catch (error) {
      console.error("Erro ao processar o PDF:", error);
    } finally {
      // Garante que o foco volte para a janela principal
      window.focus();
    }
  };

  useEffect(() => {
    fetchLotacoes();
    fetchLocaisPagamento();
  }, []);

  return (
    <>
      <Container>
        <TitleAndButton>
          <GrayTitle>Relatório de Recebimentos</GrayTitle>
          <ContainerButton>
            {blobAnalyticPDF && (
              <Button
                schemaButton="out-primary"
                action={() => imprimirRelatorio(blobAnalyticPDF)}
                disabled={isRequest}
              >
                Imprimir - Analítico
              </Button>
            )}
            {blobResumePDF && (
              <Button
                schemaButton="out-primary"
                action={() => imprimirRelatorio(blobResumePDF)}
                disabled={isRequest}
              >
                Imprimir - Resumo
              </Button>
            )}
            {(blobAnalyticPDF || blobResumePDF) && (
              <Button
                schemaButton="full-primary"
                action={() => {
                  setAppliedLotacao(undefined);
                  setAppliedLocalPagamento(undefined);
                  setAppliedDateRange([undefined, undefined]);
                  setParcelasPorContribuinte([]);
                  setDateRange([undefined, undefined]);
                  setBlobAnalyticPDF(undefined);
                  setBlobResumePDF(undefined);
                  setInvalidFields([]);
                  setSelectLotacao(undefined);
                  setSelectLocalPagamento(undefined);
                  closeReportModal();
                }}
              >
                Limpar dados aplicados
              </Button>
            )}
            {(!blobAnalyticPDF || !blobResumePDF) && (
              <Button
                schemaButton="full-primary"
                action={() => setShowModalReport(true)}
                disabled={isRequest}
              >
                Gerar Relatório
              </Button>
            )}
          </ContainerButton>
        </TitleAndButton>

        <StyledHR />

        {(appliedLotacao || (appliedDateRange[0] && appliedDateRange[1])) && (
          <AppliedFiltersWrapper>
            <i className="fa fa-filter" aria-hidden="true"></i>
            <FilterTitle>Dados aplicados:</FilterTitle>
            <FiltersContainer>
              {appliedLotacao && (
                <FilterItem>
                  <FilterKey>Lotação: </FilterKey>
                  <FilterValue>{appliedLotacao.descricao}</FilterValue>
                </FilterItem>
              )}

              {appliedLocalPagamento && (
                <FilterItem>
                  <FilterKey>Local de Pagamento: </FilterKey>
                  <FilterValue>{appliedLocalPagamento.descricao}</FilterValue>
                </FilterItem>
              )}

              {appliedDateRange[0] && appliedDateRange[1] && (
                <FilterItem>
                  <FilterKey>Período: </FilterKey>
                  <FilterValue>
                    {appliedDateRange[0].toLocaleDateString("pt-BR")} -{" "}
                    {appliedDateRange[1].toLocaleDateString("pt-BR")}
                  </FilterValue>
                </FilterItem>
              )}
            </FiltersContainer>
          </AppliedFiltersWrapper>
        )}

        {Object.keys(parcelasPorContribuinte).length === 0 ? (
          <div style={{ padding: "1rem", textAlign: "center" }}>
            Nenhum relatório gerado
          </div>
        ) : (
          Object.entries(parcelasPorContribuinte).map(
            ([contribuinteId, { contribuinte, parcelas }]) => (
              <div key={contribuinteId}>
                <div style={{ padding: "1rem", fontWeight: "bold" }}>
                  Contribuinte:{" "}
                  <span style={{ color: "#2563EB" }}>
                    {contribuinte?.nome
                      ? contribuinte.nome.toUpperCase()
                      : "NÃO IDENTIFICADO"}
                  </span>
                </div>
                <Table style={{ minHeight: "0" }}>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Nosso Número</TableHeader>
                      <TableHeader>Sequencial</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Data Vencimento</TableHeader>
                      <TableHeader>Valor Parcela</TableHeader>
                      <TableHeader>Data Pagamento</TableHeader>
                      <TableHeader>Data Caixa</TableHeader>
                      <TableHeader>Valor Pago</TableHeader>
                      <TableHeader>Valor Ajuste</TableHeader>
                      <TableHeader>Forma Pagamento</TableHeader>
                    </TableRow>
                  </TableHead>
                  <tbody>
                    {parcelas.length > 0 ? (
                      parcelas.map(
                        (parcela: GetFinancialRecordDto, index: number) => (
                          <TableRow key={index}>
                            <TableData>{parcela.nossoNumero ?? "-"}</TableData>
                            <TableData>
                              {parcela.sequencialParcela ?? "-"}
                            </TableData>
                            <TableData>
                              {parcela.statusParcela ?? "-"}
                            </TableData>
                            <TableData>
                              {parcela.dataVencimento
                                ? formatarData(parcela.dataVencimento)
                                : "00/00/0000"}
                            </TableData>
                            <TableData>
                              {parcela.valorParcela
                                ? formatCurrency(
                                    parcela.valorParcela.toString()
                                  )
                                : "R$ 0,00"}
                            </TableData>
                            <TableData>
                              {parcela.dataPagamento
                                ? formatarData(parcela.dataPagamento)
                                : "00/00/0000"}
                            </TableData>
                            <TableData>
                              {parcela.dataCaixa
                                ? formatarData(parcela.dataCaixa)
                                : "00/00/0000"}
                            </TableData>
                            <TableData>
                              {parcela.valorPago
                                ? formatCurrency(parcela.valorPago.toString())
                                : "R$ 0,00"}
                            </TableData>
                            <TableData>
                              {parcela.valorAjuste
                                ? formatCurrency(parcela.valorAjuste.toString())
                                : "R$ 0,00"}
                            </TableData>
                            <TableData>
                              {parcela.formaPagamento
                                ? parcela.formaPagamento.nome
                                : "-"}
                            </TableData>
                          </TableRow>
                        )
                      )
                    ) : (
                      <TableRow>
                        <TableData colSpan={10} style={{ textAlign: "center" }}>
                          Nenhuma parcela encontrada
                        </TableData>
                      </TableRow>
                    )}
                  </tbody>
                </Table>
              </div>
            )
          )
        )}

        <ModalContainer show={showModalReport}>
          <ModalContent style={{ marginTop: "4%" }} width={35}>
            <Button
              type="button"
              schemaButton="x-close"
              action={() => closeReportModal()}
              icon={<IoCloseSharp size={30} />}
              position="absolute"
              top={10}
              right={10}
            />
            <ModalHeader>
              <BlueTitle>Gerar relatório</BlueTitle>
            </ModalHeader>

            <ModalBody>
              <Col>
                <Label htmlFor="lotacao">Lotação</Label>
                <SelectComponent
                  color="black"
                  placeholder="Todas Lotações"
                  isPlaceHolder={false}
                  id="lotacao"
                  width={Number("100%")}
                  name="lotacao"
                  disabled={isRequest}
                  value={selectLotacao?.id?.toString() ?? ""}
                  onChange={(e) => {
                    if (e.target.value === "0") {
                      setSelectLotacao({ id: 0, descricao: "Sem lotação" });
                    } else {
                      const selectedLotacao = lotacoes.find(
                        (lotacao) => lotacao.id === parseInt(e.target.value)
                      );
                      setSelectLotacao(selectedLotacao);
                    }
                    clearError(e);
                  }}
                  options={[
                    { value: "0", label: "Sem lotação" },
                    ...lotacoes.map((lotacao) => ({
                      value: lotacao.id!.toString(),
                      label: lotacao.descricao,
                    })),
                  ]}
                />
              </Col>
              <Col>
                <Label htmlFor="localPagamento">Local de Pagamento</Label>
                <SelectComponent
                  color="black"
                  placeholder="Todas locais de pagamento"
                  isPlaceHolder={false}
                  id="localPagamento"
                  width={Number("100%")}
                  name="localPagamento"
                  disabled={isRequest}
                  value={selectLocalPagamento?.id?.toString() ?? ""}
                  onChange={(e) => {
                    const selectedLocalPagamento = locaisPagamento.find(
                      (localPagamento) =>
                        localPagamento.id === parseInt(e.target.value)
                    );
                    setSelectLocalPagamento(selectedLocalPagamento);
                    clearError(e);
                  }}
                  options={locaisPagamento.map((localPagamento) => ({
                    value: localPagamento.id!.toString(),
                    label: localPagamento.descricao,
                  }))}
                />
              </Col>
              <Col>
                <Label className="filtro-label" htmlFor="dataVencimento">
                  Data de pagamento <TextError>*</TextError>
                </Label>
                <DatePickerInputContainer
                  width={Number("100%")}
                  hasIconLeft={false}
                  hasIconRight={false}
                  error={
                    invalidFields.includes("dataVencimento") ? true : false
                  }
                  className={
                    dateRange[0] || dateRange[1] ? "has-date-content" : ""
                  }
                >
                  <DatePicker
                    selectsRange={true}
                    name="dataVencimento"
                    startDate={dateRange[0] as Date | undefined}
                    endDate={dateRange[1] as Date | undefined}
                    onChange={(dates) => {
                      handleDateRangeChange(
                        dates as [Date | undefined, Date | undefined]
                      );

                      setInvalidFields((prev) =>
                        prev.filter((field) => field !== "dataVencimento")
                      );
                    }}
                    onChangeRaw={handleDateInputChange}
                    calendarStartDay={0}
                    className={`date-picker-input placeholder-custom ${
                      dateRange[0] || dateRange[1] ? "has-value" : "no-value"
                    }`}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="00/00/0000 - 00/00/0000"
                    showPopperArrow={false}
                    popperPlacement="bottom-start"
                    isClearable={false}
                    showYearDropdown
                    showMonthDropdown
                    scrollableMonthYearDropdown
                    wrapperClassName={
                      dateRange[0] || dateRange[1]
                        ? "date-wrapper-filled"
                        : "date-wrapper-empty"
                    }
                    locale="pt-BR"
                    autoComplete="off"
                    allowSameDay
                    onKeyDown={(e) => {
                      if (e.key === "Delete" || e.key === "Backspace") {
                        setDateRange([undefined, undefined]);
                        return;
                      }
                    }}
                  />
                </DatePickerInputContainer>
                {invalidFields.includes("dataVencimento") && (
                  <TextError>
                    Preencha a data de vencimento corretamente
                  </TextError>
                )}
              </Col>
              {/* <ContainerCheckbox>
                <Checkbox
                  checked={relatorioCompleto}
                  onChange={(e) => {
                    setRelatorioCompleto(!relatorioCompleto);
                  }}
                />
                <LabelCheckbox htmlFor={`reajuste-monetario`}>
                  Imprimir somente resumo
                </LabelCheckbox>
              </ContainerCheckbox> */}
            </ModalBody>
            <ModalFooter>
              <Button
                schemaButton="full-primary"
                action={() => {
                  generateReport();
                }}
                disabled={isRequest}
              >
                Gerar Relatório
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalContainer>
      </Container>
    </>
  );
};

export default RelatorioRecebimentos;
